import { z } from 'zod';
/**
 * Tarefas e avisos da mesa (estudo da mesa). Tarefa: o que alguém da equipe precisa
 * fazer, com prazo, responsável, prioridade e, se for o caso, o paciente. Avisos: o
 * sino junta o que pede atenção agora (tarefa vencida, prazo de lead vencido, consulta
 * sem resposta, pedido atrasado, cobrança); são calculados, não guardados.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
export const CLINIC_TASK_TITLE_MAX = 120;
export const CLINIC_TASK_NOTES_MAX = 500;
export const clinicTaskSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    notes: z.string(),
    dueDay: isoDate.nullable(),
    /** Alta aparece primeiro e com marca. */
    priority: z.enum(['normal', 'alta']),
    assigneeId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    patientId: z.uuid().nullable(),
    patientName: z.string().nullable(),
    done: z.boolean(),
    doneAt: isoDateTime.nullable(),
    createdBy: z.string(),
    createdAt: isoDateTime,
});
export const createClinicTaskSchema = z.object({
    title: z.string().trim().min(2).max(CLINIC_TASK_TITLE_MAX),
    notes: z.string().trim().max(CLINIC_TASK_NOTES_MAX).optional(),
    dueDay: isoDate.optional(),
    priority: z.enum(['normal', 'alta']).optional(),
    assigneeId: z.uuid().optional(),
    patientId: z.uuid().optional(),
});
export const updateClinicTaskSchema = z
    .object({
    title: z.string().trim().min(2).max(CLINIC_TASK_TITLE_MAX),
    notes: z.string().trim().max(CLINIC_TASK_NOTES_MAX),
    dueDay: isoDate.nullable(),
    priority: z.enum(['normal', 'alta']),
    assigneeId: z.uuid().nullable(),
    patientId: z.uuid().nullable(),
    done: z.boolean(),
})
    .partial();
export const CLINIC_ALERT_KINDS = [
    'tarefa',
    'lead',
    'confirmar',
    'medicamento',
    'cobranca',
];
export const clinicAlertSchema = z.object({
    /** Estável enquanto o motivo existir (para marcar como visto no aparelho). */
    id: z.string(),
    kind: z.enum(CLINIC_ALERT_KINDS),
    title: z.string(),
    text: z.string(),
    href: z.string(),
    at: isoDateTime,
    /** Quantos itens o aviso junta (o número ao lado do lugar na barra da mesa). */
    count: z.number().int().min(0).optional(),
});
/**
 * Aviso guardado para uma pessoa da equipe (rodada 15): a menção numa nota do histórico.
 * O sino junta estes com os calculados na hora (`clinicAlertSchema`).
 */
export const clinicNotificationSchema = z.object({
    id: z.uuid(),
    kind: z.string(),
    title: z.string(),
    text: z.string(),
    href: z.string(),
    at: isoDateTime,
    read: z.boolean(),
});
export const readClinicNotificationsSchema = z.object({
    /** Sem ids: marca todos como lidos. */
    ids: z.array(z.uuid()).max(100).optional(),
});
/** Nota no histórico da pessoa, com quem avisar (rodada 15). Não se edita: corrigir é outra nota. */
export const CLINIC_NOTE_MAX = 1000;
export const createClinicNoteSchema = z.object({
    text: z.string().trim().min(2).max(CLINIC_NOTE_MAX),
    mentions: z.array(z.uuid()).max(10).optional(),
});
/**
 * Avisos de cada pessoa da equipe (rodada 15): por tipo, no sino da mesa e na conversa
 * com o Secretário. Menção: alguém citou você numa nota. Pedido: consulta pedida pela
 * página, pela Saúde ou pela conversa. Tarefa: alguém passou uma tarefa para você.
 */
export const CLINIC_NOTIFY_KINDS = [
    'mencao',
    'pedido',
    'tarefa',
    'acompanhamento',
    'recado',
];
export const CLINIC_NOTIFY_KIND_INFO = {
    mencao: { label: 'Menções', hint: 'Alguém citou você numa nota da ficha ou do lead.' },
    pedido: {
        label: 'Pedidos de consulta',
        hint: 'Consulta pedida pela página da clínica, pela Saúde ou pela conversa.',
    },
    tarefa: { label: 'Tarefas para você', hint: 'Alguém da equipe passou uma tarefa para você.' },
    acompanhamento: {
        label: 'Avisos das fichas',
        hint: 'A ficha que você assinou vai vencer ou venceu sem uma nova, e o aluno dela está há 6 dias sem treinar.',
    },
    recado: {
        label: 'Recados da equipe',
        hint: 'Outro profissional deixou um recado para você no prontuário de um paciente.',
    },
};
const notifyChannelSchema = z.object({ sino: z.boolean(), secretario: z.boolean() });
export const clinicNotifyPrefsSchema = z.object({
    mencao: notifyChannelSchema,
    pedido: notifyChannelSchema,
    tarefa: notifyChannelSchema,
    acompanhamento: notifyChannelSchema,
    recado: notifyChannelSchema,
});
export const updateClinicNotifyPrefsSchema = z.object({
    mencao: notifyChannelSchema.partial().optional(),
    pedido: notifyChannelSchema.partial().optional(),
    tarefa: notifyChannelSchema.partial().optional(),
    acompanhamento: notifyChannelSchema.partial().optional(),
    recado: notifyChannelSchema.partial().optional(),
});
/**
 * Um quadro do canal ao vivo da clínica (rodada 15): o que mudou, para as telas abertas
 * buscarem de novo. O aviso curto (`toast`) nunca leva nome de paciente.
 */
export const CLINIC_LIVE_TOPICS = [
    'agenda',
    'pacientes',
    'leads',
    'tarefas',
    'medicamentos',
    'financeiro',
    'avisos',
    'config',
];
export const clinicLiveEventSchema = z.object({
    topics: z.array(z.string()),
    by: z.string().nullable(),
    toast: z.object({ title: z.string(), text: z.string(), href: z.string() }).optional(),
});
/**
 * Marketing fase 1 (rodada 15): por origem (a primeira), quem chegou no mês e até onde foi
 * (marcou, veio, fechou tratamento, pagou), quanto já pagou e quanto custou o canal.
 * Custo por lead = gasto ÷ chegaram; por paciente novo = gasto ÷ pagaram; retorno =
 * (recebido − gasto) ÷ gasto.
 */
export const clinicMarketingRowSchema = z.object({
    origin: z.string(),
    campaign: z.string().nullable(),
    leads: z.number().int().min(0),
    scheduled: z.number().int().min(0),
    attended: z.number().int().min(0),
    treated: z.number().int().min(0),
    paying: z.number().int().min(0),
    receivedCents: z.number().int().min(0),
    spendCents: z.number().int().min(0),
    cplCents: z.number().int().min(0).nullable(),
    cacCents: z.number().int().min(0).nullable(),
    roiPercent: z.number().nullable(),
});
export const clinicMarketingSpendRowSchema = z.object({
    origin: z.string().trim().min(1).max(60),
    campaign: z.string().trim().max(80),
    amountCents: z.number().int().min(0).max(100_000_000),
});
const marketingMonth = z.string().regex(/^\d{4}-\d{2}$/);
export const clinicMarketingSchema = z.object({
    month: marketingMonth,
    rows: z.array(clinicMarketingRowSchema),
    total: clinicMarketingRowSchema,
    campaigns: z.array(clinicMarketingRowSchema),
    previous: z.object({
        month: marketingMonth,
        rows: z.array(clinicMarketingRowSchema),
        total: clinicMarketingRowSchema,
    }),
    spend: z.array(clinicMarketingSpendRowSchema),
    /** Campanhas já usadas (para sugerir ao escrever). */
    campaignNames: z.array(z.string()),
});
export const setClinicMarketingSpendSchema = z.object({
    month: marketingMonth,
    rows: z.array(clinicMarketingSpendRowSchema).max(60),
});
